import { type B2bConfig, type B2bCustomerEditable, type B2bCustomerStatus, getB2bCustomerForEdit, updateB2bCustomer, type UpdateB2bCustomerInput } from '@api/b2b';
import { listWarehousesForPicker } from '@api/work-order';
import { ActionIcon, Button, Modal, NumberInput, Select, TagsInput, TextInput } from '@mantine/core';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import styles from './b2b.module.css';
import { advanceOf, creditDaysOf, errorMessage, formatMoment, termsPreset } from './utils';

type ShipToDraft = UpdateB2bCustomerInput['shipTos'][number] & { key: string };

const STATUS: Array<{ value: B2bCustomerStatus; label: string; hint: string }> = [
    { value: 'ACTIVE', label: 'Active', hint: 'Orders normally.' },
    { value: 'BLOCKED', label: 'Blocked', hint: 'Can log in, finish orders already placed and pay for them, but cannot place new orders.' },
    { value: 'INACTIVE', label: 'Inactive', hint: 'No longer a customer: cannot log in or order, and hidden from customer lists.' },
];

/**
 * Editing a B2B customer (Settings → B2B Customers): details, billing address, commercial terms,
 * ship-to locations and status, with the history of earlier edits underneath. Changes apply from
 * now on: confirmed orders and issued PIs keep what they had.
 */
const EditCustomerModal: React.FC<{ customerId: string; config: B2bConfig; onClose: () => void }> = ({ customerId, config, onClose }) => {
    const queryClient = useQueryClient();
    const { data: current, error: loadError } = useQuery({ queryKey: ['b2b-customer-edit', customerId], queryFn: () => getB2bCustomerForEdit(customerId) });
    const { data: warehouses = [] } = useQuery({ queryKey: ['warehouses-picker-internal'], queryFn: listWarehousesForPicker, staleTime: 5 * 60 * 1000 });
    const [d, setD] = useState<(Omit<B2bCustomerEditable, 'shipTos' | 'history'> & { shipTos: ShipToDraft[] }) | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (current) setD({ ...current, shipTos: current.shipTos.map((t) => ({ ...t, gstin: t.gstin ?? undefined, key: t.warehouseId })) });
    }, [current]);

    if (loadError) {
        return (
            <Modal opened onClose={onClose} title="Edit customer" centered>
                <span className={styles.error}>{errorMessage(loadError)}</span>
            </Modal>
        );
    }
    if (!d || !current) {
        return (
            <Modal opened onClose={onClose} title="Edit customer" centered>
                <span className={styles.muted}>Loading…</span>
            </Modal>
        );
    }

    const set = (patch: Partial<typeof d>) => setD((x) => (x ? { ...x, ...patch } : x));
    const setShipTo = (key: string, patch: Partial<ShipToDraft>) => set({ shipTos: d.shipTos.map((t) => (t.key === key ? { ...t, ...patch } : t)) });
    const advance = advanceOf(d.paymentTerms);
    const creditDays = creditDaysOf(d.paymentTerms);

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const clean = (v?: string | null) => (v && v.trim() ? v.trim() : undefined);
            await updateB2bCustomer(customerId, {
                name: d.name,
                legalName: clean(d.legalName),
                gstin: clean(d.gstin)?.toUpperCase(),
                segment: d.segment,
                tags: d.tags,
                email: d.email ?? '',
                phone: clean(d.phone),
                billing: d.billing,
                shipFromWarehouseId: clean(d.shipFromWarehouseId),
                minOrderValue: d.minOrderValue ?? undefined,
                paymentTerms: d.paymentTerms,
                status: d.status,
                shipTos: d.shipTos.map(({ key: _k, ...t }) => ({ ...t, gstin: clean(t.gstin), warehouseId: t.warehouseId || undefined })),
            });
            await Promise.all(
                ['b2b-customers', 'b2b-customer', 'b2b-customer-edit', 'b2b-catalog'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })),
            );
            onClose();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const ready =
        d.name.trim() &&
        (d.email ?? '').trim() &&
        d.billing.street &&
        d.billing.city &&
        d.billing.state &&
        d.billing.zip &&
        d.shipTos.length > 0 &&
        d.shipTos.every((t) => t.name.trim() && t.street && t.city && t.state && t.zip);

    return (
        <Modal opened onClose={onClose} title={`Edit ${current.name}`} centered size="xl">
            <div className={styles.stack}>
                <span className={styles.cardTitle}>Details</span>
                <div className={styles.fieldGrid}>
                    <TextInput label="Name" required value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} />
                    <TextInput label="Legal name" value={d.legalName ?? ''} onChange={(e) => set({ legalName: e.currentTarget.value })} />
                    <TextInput label="GSTIN" value={d.gstin ?? ''} onChange={(e) => set({ gstin: e.currentTarget.value })} />
                    <TextInput label="Email (PIs and dispatch updates)" required value={d.email ?? ''} onChange={(e) => set({ email: e.currentTarget.value })} />
                    <TextInput label="Phone" value={d.phone ?? ''} onChange={(e) => set({ phone: e.currentTarget.value })} />
                    <Select
                        label="Status"
                        description={STATUS.find((s) => s.value === d.status)?.hint}
                        inputWrapperOrder={['label', 'input', 'description', 'error']}
                        data={STATUS.map(({ value, label }) => ({ value, label }))}
                        value={d.status}
                        onChange={(v) => v && set({ status: v as B2bCustomerStatus })}
                    />
                </div>

                <span className={styles.cardTitle}>Commercial</span>
                <div className={styles.fieldGrid}>
                    <Select
                        label="Segment"
                        description="Decides the segment prices and schemes from the next order on."
                        inputWrapperOrder={['label', 'input', 'description', 'error']}
                        data={config.segments.map((s) => ({ value: s.key, label: s.label }))}
                        value={d.segment}
                        onChange={(v) => v && set({ segment: v })}
                    />
                    <TagsInput label="Tags" value={d.tags} onChange={(v) => set({ tags: v })} />
                    <NumberInput
                        label="Advance at order (%)"
                        description="Applies to orders whose PI is confirmed from now on."
                        inputWrapperOrder={['label', 'input', 'description', 'error']}
                        min={0}
                        max={100}
                        allowDecimal={false}
                        value={advance}
                        onChange={(v) => set({ paymentTerms: termsPreset(Number(v) || 0, creditDays || 30) })}
                    />
                    <NumberInput
                        label="Credit days after dispatch"
                        min={0}
                        allowDecimal={false}
                        disabled={advance >= 100}
                        value={advance >= 100 ? '' : creditDays}
                        onChange={(v) => set({ paymentTerms: termsPreset(advance, Number(v) || 0) })}
                    />
                    <NumberInput
                        label="Minimum order value"
                        description="Blank: the segment's minimum applies."
                        inputWrapperOrder={['label', 'input', 'description', 'error']}
                        min={0}
                        value={d.minOrderValue ?? ''}
                        onChange={(v) => set({ minOrderValue: v === '' ? null : Number(v) })}
                    />
                    <Select
                        label="Ships from"
                        data={warehouses.map((w) => ({ value: w.warehouseId, label: w.name }))}
                        value={d.shipFromWarehouseId}
                        onChange={(v) => set({ shipFromWarehouseId: v })}
                        placeholder={config.defaultShipFromWarehouseId ? 'Brand default' : 'Choose at dispatch'}
                        clearable
                    />
                </div>

                <span className={styles.cardTitle}>Billing address</span>
                <div className={styles.fieldGrid}>
                    <TextInput label="Street" required value={d.billing.street} onChange={(e) => set({ billing: { ...d.billing, street: e.currentTarget.value } })} />
                    <TextInput label="City" required value={d.billing.city} onChange={(e) => set({ billing: { ...d.billing, city: e.currentTarget.value } })} />
                    <TextInput label="State" required value={d.billing.state} onChange={(e) => set({ billing: { ...d.billing, state: e.currentTarget.value } })} />
                    <TextInput label="PIN code" required value={d.billing.zip} onChange={(e) => set({ billing: { ...d.billing, zip: e.currentTarget.value } })} />
                </div>

                <span className={styles.cardTitle}>Ship-to locations</span>
                <span className={styles.muted}>Removing a location keeps it on orders that already ship there; it just cannot be chosen for new ones.</span>
                {d.shipTos.map((t, i) => (
                    <div key={t.key} className={styles.card}>
                        <div className={styles.between}>
                            <span className={styles.fieldValue}>{t.name || `New location ${i + 1}`}</span>
                            <ActionIcon variant="subtle" color="gray" aria-label="Remove location" disabled={d.shipTos.length === 1} onClick={() => set({ shipTos: d.shipTos.filter((x) => x.key !== t.key) })}>
                                <TrashIcon size={16} />
                            </ActionIcon>
                        </div>
                        <div className={styles.fieldGrid}>
                            <TextInput label="Location name" required value={t.name} onChange={(e) => setShipTo(t.key, { name: e.currentTarget.value })} />
                            <TextInput label="GSTIN, if registered separately" value={t.gstin ?? ''} onChange={(e) => setShipTo(t.key, { gstin: e.currentTarget.value })} />
                            <TextInput label="Street" required value={t.street} onChange={(e) => setShipTo(t.key, { street: e.currentTarget.value })} />
                            <TextInput label="City" required value={t.city} onChange={(e) => setShipTo(t.key, { city: e.currentTarget.value })} />
                            <TextInput label="State" required value={t.state} onChange={(e) => setShipTo(t.key, { state: e.currentTarget.value })} />
                            <TextInput label="PIN code" required value={t.zip} onChange={(e) => setShipTo(t.key, { zip: e.currentTarget.value })} />
                        </div>
                    </div>
                ))}
                <Button
                    size="xs"
                    variant={ButtonVariant.SUBTLE}
                    leftSection={<PlusIcon size={14} />}
                    w="fit-content"
                    onClick={() => set({ shipTos: [...d.shipTos, { key: `new-${Date.now()}`, name: '', street: '', city: '', state: '', zip: '' }] })}
                >
                    Add ship-to location
                </Button>

                {error ? <span className={styles.error}>{error}</span> : null}
                <div className={styles.rowEnd}>
                    <Button variant={ButtonVariant.DEFAULT} disabled={busy} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant={ButtonVariant.ACTION} loading={busy} disabled={!ready} onClick={() => void save()}>
                        Save changes
                    </Button>
                </div>

                <span className={styles.cardTitle}>History</span>
                {current.history.length === 0 ? <span className={styles.muted}>No edits yet.</span> : null}
                {current.history.map((h) => (
                    <div key={h.at} className={styles.stack}>
                        <span className={styles.fieldValue}>
                            {formatMoment(h.at)} · {h.by}
                        </span>
                        {h.changes.map((c) => (
                            <span key={c.field} className={styles.cardHint}>
                                {c.field}: {c.before} → {c.after}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </Modal>
    );
};

export default EditCustomerModal;
